import React, { useState, useEffect, useRef } from 'react';
import { 
  Sun, 
  Moon, 
  Search, 
  Bell, 
  ShieldCheck, 
  LogOut, 
  UserCheck, 
  Sparkles, 
  ChevronDown, 
  KeyRound, 
  Smartphone, 
  Lock, 
  User,
  MessageSquare,
  Users,
  Activity
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { AdminRole } from '../../types/admin';
import { 
  startPresenceHeartbeat, 
  setAdminPresence, 
  subscribeToAdminPresence, 
  AdminPresenceRecord, 
  PresenceStatus 
} from '../../services/adminPresenceService';
import { AdminChatDrawer } from '../chat/AdminChatDrawer';

export type ProfileTab = 'profile' | '2fa' | 'password';

interface HeaderProps {
  currentViewTitle: string;
  onOpenNotifications?: () => void;
  unreadCount?: number;
  onNavigateToProfile?: (tab: ProfileTab) => void;
  onNavigateToChat?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentViewTitle,
  onOpenNotifications,
  unreadCount = 3,
  onNavigateToProfile,
  onNavigateToChat,
}) => {
  const { isDark, toggleTheme } = useTheme();
  const { currentUser, logout } = useAuth();
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [presenceMenuOpen, setPresenceMenuOpen] = useState(false);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [myStatus, setMyStatus] = useState<PresenceStatus>('online');
  const [activeAdmins, setActiveAdmins] = useState<AdminPresenceRecord[]>([]);

  const presenceMenuRef = useRef<HTMLDivElement>(null);

  // Heartbeat & Online Presence
  useEffect(() => {
    if (!currentUser) return;
    const cleanupHeartbeat = startPresenceHeartbeat(
      currentUser.uid,
      currentUser.email,
      currentUser.displayName || currentUser.email.split('@')[0],
      currentUser.role || 'super_admin',
      currentUser.avatarUrl
    );

    const unsubPresence = subscribeToAdminPresence((records) => {
      setActiveAdmins(records);
      const me = records.find((r) => r.uid === currentUser.uid);
      if (me) setMyStatus(me.status);
    });

    return () => {
      cleanupHeartbeat();
      unsubPresence();
    };
  }, [currentUser]);

  // Click outside to close presence menu
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (presenceMenuRef.current && !presenceMenuRef.current.contains(e.target as Node)) {
        setPresenceMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChangeStatus = async (newStatus: PresenceStatus) => {
    if (!currentUser) return;
    setMyStatus(newStatus);
    await setAdminPresence(
      currentUser.uid,
      currentUser.email,
      currentUser.displayName || currentUser.email.split('@')[0],
      currentUser.role || 'super_admin',
      newStatus,
      currentUser.avatarUrl
    );
    setPresenceMenuOpen(false);
  };

  const onlineCount = activeAdmins.filter((a) => a.status === 'online' || a.status === 'away').length;

  return (
    <>
      <header className="sticky top-0 z-30 flex h-20 items-center justify-between px-6 sm:px-8 backdrop-blur-xl border-b transition-colors bg-[#10231d]/95 border-[#22493c] text-[#f5f5f0]">
        {/* View Title & Admin Presence Indicator */}
        <div className="flex items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
            {currentViewTitle}
          </h1>

          {/* Replaced 'Mainnet Europe-West1' with live interactive Admin Online/Offline presence */}
          <div className="relative" ref={presenceMenuRef}>
            <button
              onClick={() => setPresenceMenuOpen(!presenceMenuOpen)}
              className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all border shadow-sm ${
                myStatus === 'online'
                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
                  : myStatus === 'away'
                    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25'
                    : 'bg-slate-500/15 text-slate-300 border-slate-500/30 hover:bg-slate-500/25'
              }`}
              title="Click to change your admin online status"
            >
              <span className={`w-2 h-2 rounded-full ${
                myStatus === 'online' ? 'bg-emerald-400 animate-pulse' : myStatus === 'away' ? 'bg-amber-400' : 'bg-slate-400'
              }`} />
              <span className="capitalize">{myStatus}</span>
              <span className="text-[10px] text-[#90cbb7]/80 font-semibold pl-1 border-l border-[#22493c]">
                {onlineCount} {onlineCount === 1 ? 'Admin Active' : 'Admins Active'}
              </span>
              <ChevronDown className="w-3 h-3 ml-0.5 opacity-60" />
            </button>

            {/* Presence Status Dropdown */}
            {presenceMenuOpen && (
              <div className="absolute left-0 mt-2 w-72 rounded-2xl bg-[#10231d] border border-[#22493c] shadow-2xl p-3 z-50 text-xs">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#90cbb7] px-2 mb-2">
                  My Staff Presence
                </div>
                <div className="space-y-1 mb-3">
                  {[
                    { id: 'online', label: 'Online', note: 'Available for disputes & tasks', dot: 'bg-emerald-400' },
                    { id: 'away', label: 'Away', note: 'Temporarily stepping out', dot: 'bg-amber-400' },
                    { id: 'offline', label: 'Offline', note: 'Appear offline to team', dot: 'bg-slate-400' },
                  ].map((st) => (
                    <button
                      key={st.id}
                      onClick={() => handleChangeStatus(st.id as PresenceStatus)}
                      className={`w-full flex items-center justify-between p-2 rounded-xl text-left transition-colors ${
                        myStatus === st.id 
                          ? 'bg-brand-500/20 text-white font-bold border border-brand-500/30' 
                          : 'hover:bg-[#152a23] text-[#90cbb7]'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                        <div>
                          <div className="font-bold text-white text-xs">{st.label}</div>
                          <div className="text-[10px] text-[#90cbb7]/70">{st.note}</div>
                        </div>
                      </div>
                      {myStatus === st.id && <span className="text-brand-400 text-xs font-bold">✓</span>}
                    </button>
                  ))}
                </div>

                {/* Team Roster Snippet */}
                <div className="pt-2.5 border-t border-[#22493c]">
                  <div className="flex items-center justify-between px-2 mb-1.5">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#90cbb7]">
                      Staff On Duty ({onlineCount})
                    </span>
                    <button
                      onClick={() => {
                        setPresenceMenuOpen(false);
                        setChatDrawerOpen(true);
                      }}
                      className="text-[10px] text-brand-400 hover:underline font-bold"
                    >
                      Open Chat
                    </button>
                  </div>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                    {activeAdmins.filter(a => a.status !== 'offline').slice(0, 4).map((adm) => (
                      <div key={adm.uid} className="flex items-center justify-between p-1.5 rounded-lg bg-[#152a23]/60">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${adm.status === 'online' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                          <span className="truncate text-[11px] font-medium text-white">{adm.displayName}</span>
                        </div>
                        <span className="text-[9px] text-[#90cbb7] capitalize shrink-0 ml-2">{adm.role.replace('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Search input */}
          <div className="relative hidden lg:block w-64">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#90cbb7]" />
            <input
              type="text"
              placeholder="Search users, escrows, IDs..."
              className="w-full pl-9 pr-4 py-2 rounded-full text-xs outline-none transition-colors border
                bg-[#152a23] border-[#22493c] text-white placeholder-[#90cbb7]/60 focus:border-brand-500"
            />
          </div>

          {/* Theme Toggle Button (Light/Dark mode) */}
          <button
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            title={isDark ? 'Switch to Light Mode ☀️' : 'Switch to Dark Mode 🌙'}
            className="relative p-2.5 rounded-full transition-all duration-300 border
              bg-[#152a23] border-[#22493c] text-[#90cbb7] hover:text-brand-400 hover:border-brand-500/40"
          >
            {isDark ? (
              <Sun className="w-5 h-5 text-amber-300 animate-spin-slow" />
            ) : (
              <Moon className="w-5 h-5 text-emerald-400" />
            )}
          </button>

        {/* Admin Team Chat Button */}
        <button
          onClick={() => setChatDrawerOpen(true)}
          title="Open Admin Team Chat & Shift Handover"
          className="relative p-2.5 rounded-full transition-colors border
            bg-[#152a23] border-[#22493c] text-[#90cbb7] hover:text-white hover:border-brand-500/40"
        >
          <MessageSquare className="w-5 h-5 text-brand-400" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-400" />
        </button>

        {/* Notifications Icon */}
        <button
          onClick={onOpenNotifications}
          className="relative p-2.5 rounded-full transition-colors border
            bg-[#152a23] border-[#22493c] text-[#90cbb7] hover:text-white"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-brand-500 shadow-glow-sm" />
          )}
        </button>

        {/* Role Selector & Profile */}
        <div className="relative">
          <button
            onClick={() => setRoleMenuOpen(!roleMenuOpen)}
            className="flex items-center gap-3 p-1.5 sm:px-3 sm:py-1.5 rounded-full transition-all border
              bg-[#152a23] border-[#22493c] hover:border-brand-500/30"
          >
            <div className="w-8 h-8 rounded-full bg-brand-500/20 text-brand-400 flex items-center justify-center font-bold text-sm border border-brand-500/30 overflow-hidden">
              {currentUser?.avatarUrl ? (
                <img src={currentUser.avatarUrl} alt={currentUser.displayName} className="w-full h-full object-cover" />
              ) : (
                currentUser?.displayName?.charAt(0) || 'A'
              )}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold text-white leading-tight">
                {currentUser?.displayName || 'Super Admin'}
              </p>
              <p className="text-[10px] font-medium text-brand-400 capitalize">
                {currentUser?.role.replace('_', ' ')}
              </p>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-[#90cbb7] hidden sm:block" />
          </button>

          {/* Role switcher popover */}
          {roleMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-2xl p-2 shadow-ambient z-50 border
              bg-[#152a23] border-[#22493c]">
              {/* Profile info & quick edit */}
              <button
                type="button"
                onClick={() => {
                  onNavigateToProfile?.('profile');
                  setRoleMenuOpen(false);
                }}
                className="w-full p-3 border-b border-[#22493c] flex items-center gap-3 text-left hover:bg-brand-500/10 transition-colors rounded-t-2xl"
              >
                <div className="w-9 h-9 rounded-full overflow-hidden border border-brand-500/40 shrink-0">
                  {currentUser?.avatarUrl ? (
                    <img src={currentUser.avatarUrl} alt={currentUser.displayName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-brand-500/20 text-brand-400 flex items-center justify-center font-bold text-xs">
                      {currentUser?.displayName?.charAt(0) || 'A'}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-white truncate">
                    {currentUser?.displayName}
                  </p>
                  <p className="text-[10px] text-[#90cbb7] truncate">
                    {currentUser?.email}
                  </p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-brand-500/15 text-brand-400 capitalize">
                    {currentUser?.role.replace('_', ' ')}
                  </span>
                </div>
              </button>

              <div className="p-1.5 border-b border-[#22493c]">
                <button
                  type="button"
                  onClick={() => {
                    onNavigateToProfile?.('profile');
                    setRoleMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-brand-400 hover:bg-brand-500/10 transition-colors text-left"
                >
                  <User className="w-4 h-4" />
                  <span>Edit Profile & Avatar</span>
                </button>
              </div>

              <div className="mt-2 pt-2 border-t border-[#22493c]">
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#90cbb7]/70">
                  Account Security
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onNavigateToProfile?.('2fa');
                    setRoleMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-[#90cbb7] hover:bg-[#22493c] hover:text-white transition-colors text-left"
                >
                  <span className="flex items-center gap-2">
                    <Smartphone className="w-3.5 h-3.5 text-brand-400" />
                    2FA Authenticator
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-400 border border-brand-500/30">
                    Setup
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    onNavigateToProfile?.('password');
                    setRoleMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-[#90cbb7] hover:bg-[#22493c] hover:text-white transition-colors text-left"
                >
                  <span className="flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                    Reset Password
                  </span>
                </button>
              </div>

              <div className="mt-2 pt-2 border-t border-[#22493c]">
                <button
                  type="button"
                  onClick={() => {
                    logout();
                    setRoleMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>

      {/* Admin Team Chat & Shift Handover Slide-over Drawer */}
      <AdminChatDrawer
        isOpen={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        onOpenFullView={onNavigateToChat}
      />
    </>
  );
};

